// MonthlyReportPage — paid-tier in-app surface for the monthly coaching report.
// Lists all historical reports (most recent first) and renders the selected
// one as four cards plus a reflective-question input.
//
// Source: CogniPrint_Recalibration_Cadence_Research_v2.md §8,
// CogniPrint_Recalibration_Dev_Plan_v1.md §3.9 (REC-040).

import { useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { useAuthStore } from '@shared/state';
import {
  listMonthlyReports,
  markMonthlyReportOpened,
  submitReflectiveAnswer,
  type MonthlyReport,
} from '@shared/services/recalibration-api';
import { trackEvent, getBaseProperties } from '@shared/services/analytics';
import { ANALYTICS_EVENTS } from '@shared/constants';
import { THICK_TILE_STYLE } from '../components/dashboard/tileStyles';

function formatYearMonth(yearMonth: string): string {
  const [y, m] = yearMonth.split('-').map((n) => parseInt(n, 10));
  const date = new Date(Date.UTC(y, m - 1, 1));
  return date.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
}

export function MonthlyReportPage() {
  const user = useAuthStore((s) => s.user);
  const [params, setParams] = useSearchParams();
  const [reports, setReports] = useState<MonthlyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    void listMonthlyReports(user.uid).then((rs) => {
      if (cancelled) return;
      setReports(rs);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const selectedYearMonth = params.get('m') ?? reports[0]?.yearMonth ?? '';
  const selected: MonthlyReport | null = useMemo(
    () => reports.find((r) => r.yearMonth === selectedYearMonth) ?? null,
    [reports, selectedYearMonth],
  );

  useEffect(() => {
    if (!user || !selected || selected.openedAt) return;
    void markMonthlyReportOpened(user.uid, selected.yearMonth);
    trackEvent({
      name: ANALYTICS_EVENTS.RECAL_MONTHLY_REPORT_OPENED,
      properties: {
        ...getBaseProperties(),
        yearMonth: selected.yearMonth,
        surface: 'in_app',
      },
    });
  }, [user, selected]);

  useEffect(() => {
    setAnswer(selected?.reflectiveAnswer ?? '');
    setSubmitted(!!selected?.reflectiveAnswer);
  }, [selected]);

  if (!user) return <Navigate to="/login" replace />;

  async function onSubmitReflective() {
    if (!selected || !answer.trim()) return;
    setSubmitting(true);
    try {
      await submitReflectiveAnswer({
        yearMonth: selected.yearMonth,
        answer: answer.trim(),
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-dark-bg text-content">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="font-heading font-bold text-2xl mb-1">Monthly Report</h1>
        <p className="text-content-muted text-sm mb-6">
          A short coaching brief generated from the last 30 days of your
          CogniPrint activity.
        </p>

        {loading && (
          <div className="py-10 text-center text-content-muted">Loading…</div>
        )}

        {!loading && reports.length === 0 && (
          <div
            className="relative overflow-hidden rounded-xl border border-indigo-400/15 p-6 text-content-muted text-sm"
            style={THICK_TILE_STYLE}
          >
            Your first monthly report will arrive on your signup-day-of-month.
            Paid-tier feature.
          </div>
        )}

        {reports.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-5">
            {reports.map((r) => (
              <button
                key={r.yearMonth}
                onClick={() => setParams({ m: r.yearMonth })}
                className="text-xs font-medium rounded-full transition-colors"
                style={{
                  padding: '6px 12px',
                  background:
                    r.yearMonth === selectedYearMonth
                      ? 'rgba(108,92,231,0.18)'
                      : 'rgba(255,255,255,0.03)',
                  border:
                    r.yearMonth === selectedYearMonth
                      ? '1px solid rgba(108,92,231,0.45)'
                      : '1px solid rgba(255,255,255,0.08)',
                  color:
                    r.yearMonth === selectedYearMonth ? '#A39BE0' : '#c0bdd4',
                }}
              >
                {formatYearMonth(r.yearMonth)}
              </button>
            ))}
          </div>
        )}

        {selected && (
          <div className="space-y-4">
            {selected.lastMonthLoopClose && (
              <div
                className="relative overflow-hidden rounded-xl p-4 border border-gold-500/30"
                style={THICK_TILE_STYLE}
              >
                <div className="text-[10px] uppercase tracking-wider text-gold-500 font-semibold mb-1">
                  Close the loop
                </div>
                <p className="text-sm text-content-secondary m-0 leading-relaxed">
                  {selected.lastMonthLoopClose}
                </p>
              </div>
            )}

            {/* One thing to try */}
            <div
              className="relative overflow-hidden rounded-xl p-5 border border-indigo-500/30"
              style={THICK_TILE_STYLE}
            >
              <div className="text-[10px] uppercase tracking-wider text-indigo-300 font-semibold mb-1">
                One thing to try this month
              </div>
              <h3 className="font-heading font-bold text-[16px] text-white mb-2 leading-snug">
                {selected.experiment.title}
              </h3>
              <p className="text-sm text-content-secondary m-0 leading-relaxed">
                {selected.experiment.description}
              </p>
            </div>

            {/* Next up */}
            <div
              className="relative overflow-hidden rounded-xl p-5 border border-indigo-400/15"
              style={THICK_TILE_STYLE}
            >
              <div className="text-[10px] uppercase tracking-wider text-content-dim font-semibold mb-3">
                Next up for you
              </div>
              <ul className="space-y-3 m-0 p-0 list-none">
                {selected.nextUp.map((n, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm text-content-secondary"
                  >
                    <ArrowRight
                      size={14}
                      className="text-indigo-300 shrink-0 mt-1"
                    />
                    <div>
                      <div className="font-semibold text-white mb-0.5">
                        {n.title}
                      </div>
                      <div className="text-content-muted text-[13px] leading-snug">
                        {n.rationale}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Profile evolution preview */}
            <div
              className="relative overflow-hidden rounded-xl p-5 border border-indigo-400/15"
              style={THICK_TILE_STYLE}
            >
              <div className="text-[10px] uppercase tracking-wider text-content-dim font-semibold mb-2">
                Profile evolution preview
              </div>
              <p className="text-sm text-content-secondary m-0 leading-relaxed">
                {selected.profileEvolutionPreview}
              </p>
            </div>

            {/* Reflective question */}
            <div
              className="relative overflow-hidden rounded-xl p-5 border border-indigo-500/30"
              style={THICK_TILE_STYLE}
            >
              <div className="text-[10px] uppercase tracking-wider text-indigo-300 font-semibold mb-2">
                One reflective question
              </div>
              <p className="font-heading font-semibold text-white text-[15px] mb-3 leading-snug">
                {selected.reflectiveQuestion}
              </p>
              <textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="In your own words…"
                rows={4}
                disabled={submitted || submitting}
                className="w-full rounded-lg text-sm p-3 mb-3"
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: '#e1dfef',
                  resize: 'none',
                }}
              />
              <div className="flex items-center justify-end">
                <button
                  onClick={onSubmitReflective}
                  disabled={!answer.trim() || submitting || submitted}
                  className="rounded-lg font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    padding: '9px 18px',
                    background: 'linear-gradient(135deg, #6C5CE7, #A39BE0)',
                    color: '#fff',
                  }}
                >
                  {submitted ? (
                    <>
                      <Check size={14} className="inline mr-1" /> Sent
                    </>
                  ) : submitting ? (
                    'Sending…'
                  ) : (
                    'Submit'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
